import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext.js';
import apiService from '../services/api.js';

const LoginPage = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [googleLoading, setGoogleLoading] = useState(false);
  const [googleLoaded, setGoogleLoaded] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();

  // 获取重定向目标路径，如果没有则默认跳转到首页
  const from = location.state?.from || '/';

  // 检查Google客户端ID是否配置
  const isGoogleConfigured = process.env.REACT_APP_GOOGLE_CLIENT_ID &&
                           process.env.REACT_APP_GOOGLE_CLIENT_ID !== 'your_google_client_id_here';

  // 点击Google登录按钮后的处理
  const handleGoogleLoginClick = () => {
    setGoogleLoading(true);
    setError('');

    // 如果Google脚本已经加载过，直接触发账户选择
    if (window.google && window.google.accounts) {
      triggerGoogleSignin();
      return;
    }

    // 否则动态加载Google脚本
    loadGoogleScript();
  };

  const loadGoogleScript = () => {
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => {
      setGoogleLoaded(true);
      triggerGoogleSignin();
    };
    script.onerror = () => {
      setError('Google登录服务加载失败，请检查网络连接');
      setGoogleLoading(false);
    };
    document.head.appendChild(script);
  };

  const initializeGoogleSignin = () => {
    try {
      // 初始化Google Sign-In - 强制使用正确的客户端ID
      const clientId = '51293332725-gc1qdhebstrao93bht2oi7beh15qjtqb.apps.googleusercontent.com';
      console.log('🔧 Google Client ID (forced):', clientId);
      console.log('🔧 Environment REACT_APP_GOOGLE_CLIENT_ID:', process.env.REACT_APP_GOOGLE_CLIENT_ID);

      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: handleGoogleResponse,
        auto_select: false,
        cancel_on_tap_outside: false,
        prompt_parent_id: 'google-signin-container', // 指定弹出窗口的父容器
        ux_mode: 'popup' // 使用弹窗模式而不是重定向
      });
    } catch (error) {
      console.error('Google Sign-In initialization failed:', error);
      setGoogleLoading(false);
      if (error.message && error.message.includes('origin is not allowed')) {
        setError('Google OAuth 配置错误。请查看 design/google-oauth-quick-fix.md 修复配置');
      } else {
        setError('Google登录初始化失败');
      }
    }
  };

  const triggerGoogleSignin = () => {
    try {
      // 先初始化Google登录
      initializeGoogleSignin();

      // 创建一个隐藏的Google按钮并触发点击
      if (window.google && window.google.accounts) {
        // 创建隐藏的容器
        const hiddenContainer = document.createElement('div');
        hiddenContainer.style.cssText = 'position:fixed;top:0;left:0;width:1px;height:1px;overflow:hidden;opacity:0;';
        document.body.appendChild(hiddenContainer);

        // 渲染Google按钮
        window.google.accounts.id.renderButton(hiddenContainer, {
          theme: 'filled_black',
          size: 'large',
          text: 'signin_with',
          shape: 'rectangular',
          logo_alignment: 'left'
        });

        // 等待按钮渲染完成后点击
        setTimeout(() => {
          const googleButton = hiddenContainer.querySelector('div[role="button"]');
          if (googleButton) {
            // 模拟点击
            const clickEvent = new MouseEvent('click', {
              view: window,
              bubbles: true,
              cancelable: true
            });
            googleButton.dispatchEvent(clickEvent);
          }

          // 清理隐藏容器
          setTimeout(() => {
            if (document.body.contains(hiddenContainer)) {
              document.body.removeChild(hiddenContainer);
            }
          }, 1000);
        }, 100);
      }

      setGoogleLoading(false);
    } catch (error) {
      console.error('Google Sign-In failed:', error);
      setError('Google登录失败');
      setGoogleLoading(false);
    }
  };

  const handleGoogleResponse = async (response) => {
    try {
      setIsLoading(true);
      setError('');

      // 发送ID token到后端验证
      const result = await apiService.googleOAuthLogin(response.credential);

      if (result.success) {
        const { user, accessToken, refreshToken } = result.data;

        // 使用AuthContext的login方法更新全局状态
        login(user, accessToken, refreshToken);

        // 跳转到原始访问的页面或主页
        navigate(from, { replace: true });
      } else {
        setError('登录失败，请重试');
      }
    } catch (error) {
      console.error('Google login failed:', error);
      setError('登录失败，请重试');
    } finally {
      setIsLoading(false);
      setGoogleLoading(false);
    }
  };

  
  return (
    <div className="min-h-screen bg-black flex items-center justify-center px-4">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-white">
            欢迎访问 Easy Record
          </h2>
          <p className="mt-4 text-center text-lg text-blue-400 font-medium">
            你负责想，我负责写
          </p>
          <p className="mt-2 text-center text-sm text-gray-400">
            使用Google账户登录以继续使用
          </p>
        </div>

        <div className="mt-8 space-y-6">
          {error && (
            <div className="bg-red-500 bg-opacity-20 border border-red-500 text-red-200 px-4 py-3 rounded-md">
              {error}
            </div>
          )}

          <div className="flex flex-col items-center space-y-4">
            {/* 静态Google登录按钮 */}
            <button
              onClick={handleGoogleLoginClick}
              disabled={googleLoading || isLoading}
              className="w-full max-w-xs bg-white hover:bg-gray-100 text-gray-800 font-semibold py-3 px-4 rounded-lg border border-gray-300 flex items-center justify-center space-x-3 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {googleLoading ? (
                <>
                  <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                  <span>正在连接Google...</span>
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  <span>使用 Google 账户登录</span>
                </>
              )}
            </button>

            {googleLoading && (
              <div className="text-gray-400 text-sm">
                正在连接Google服务，请稍候...
              </div>
            )}
          </div>

          <div className="text-center">
            <p className="text-xs text-gray-500">
              登录即表示您同意我们的服务条款和隐私政策
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;